import React, { useEffect, useState } from 'react'
import {
  FaCar,
  FaHotel,
  FaUserTie,
  FaMapMarkedAlt,
  FaTicketAlt,
  FaCalculator,
  FaTrash,
  FaCheck,
  FaRoute,
  FaMountain,
  FaStar,
  FaCalendarAlt,
  FaUsers,
  FaCampground,
  FaHiking,
} from 'react-icons/fa'
import tourService from '../services/tourService'
import serviceService from '../services/serviceService'
import discountService from '../services/discountService'
import bookingService from '../services/bookingService'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Seo from '../utils/SEO'
import MapTripPlanner from '../components/MapTripPlanner'
import Button from '../components/ui/Button'

const TourPlanner = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Selection states
  const [selectedTour, setSelectedTour] = useState(null)
  const [selectedCar, setSelectedCar] = useState(null)
  const [selectedGuide, setSelectedGuide] = useState(null)
  const [selectedHotel, setSelectedHotel] = useState(null)
  const [selectedCampSite, setSelectedCampSite] = useState(null)
  const [selectedActivities, setSelectedActivities] = useState([])

  // Available options
  const [tours, setTours] = useState([])
  const [cars, setCars] = useState([])
  const [guides, setGuides] = useState([])
  const [hotels, setHotels] = useState([])
  const [campSites, setCampSites] = useState([])
  const [activityServices, setActivityServices] = useState([])

  // Booking parameters
  const [numberOfPersons, setNumberOfPersons] = useState(1)
  const [numberOfDays, setNumberOfDays] = useState(1)
  const [startDate, setStartDate] = useState('')

  // Pricing
  const [subtotal, setSubtotal] = useState(0)
  const [couponCode, setCouponCode] = useState('')
  const [appliedDiscount, setAppliedDiscount] = useState(null)
  const [finalAmount, setFinalAmount] = useState(0)

  // UI states
  const [plannerMode, setPlannerMode] = useState('package') // 'package' or 'custom'
  const [activeSection, setActiveSection] = useState('tour')
  const [loading, setLoading] = useState(false)
  const [validatingCoupon, setValidatingCoupon] = useState(false)
  const [serviceRemarks, setServiceRemarks] = useState({})
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    loadAllData()
    restoreSelections()
  }, [])

  // Restore selections after login — show toast instead of immediately opening modal
  useEffect(() => {
    if (user) {
      const pendingBooking = localStorage.getItem('pendingTourPlannerBooking')
      if (pendingBooking) {
        const savedData = JSON.parse(pendingBooking)
        if (savedData.savedSelections) {
          toast.success(
            'Your selections have been restored. Review and click "Book Now" to proceed.',
          )
        }
      }
    }
  }, [user])

  useEffect(() => {
    calculatePricing()
  }, [
    selectedTour,
    selectedCar,
    selectedGuide,
    selectedHotel,
    selectedCampSite,
    selectedActivities,
    numberOfPersons,
    numberOfDays,
    appliedDiscount,
  ])

  // Reload camp sites and activities filtered by tour's location when tour changes
  useEffect(() => {
    const loadLocationServices = async () => {
      const locationId = selectedTour?.location_id
      const params = locationId ? { limit: 100, location_id: locationId } : { limit: 100 }
      try {
        const [campSiteData, activityData] = await Promise.all([
          serviceService.getAllServices({ type: 'camp_site', ...params }),
          serviceService.getAllServices({ type: 'activity', ...params }),
        ])
        setCampSites(campSiteData.services || [])
        setActivityServices(activityData.services || [])
        // Clear selections if they no longer match the filtered list
        if (selectedCampSite && locationId && selectedCampSite.location_id !== locationId) {
          setSelectedCampSite(null)
        }
        if (selectedActivities.length > 0 && locationId) {
          const validIds = new Set((activityData.services || []).map((s) => s.id))
          setSelectedActivities((prev) => prev.filter((a) => validIds.has(a.id)))
        }
      } catch (error) {
        console.error('Error loading location services:', error)
      }
    }
    loadLocationServices()
  }, [selectedTour?.id, selectedTour?.location_id])

  const getItemLabel = (item) => item?.title || item?.name || 'Selected service'

  const getBasePrice = (item) => Number(item?.base_price || 0)

  const getHotelRoomsNeeded = (hotel = selectedHotel, persons = numberOfPersons) => {
    if (!hotel) return 0
    const capacity = Math.max(1, Number(hotel.capacity) || 1)
    return Math.max(1, Math.ceil(persons / capacity))
  }

  const loadAllData = async () => {
    try {
      setLoading(true)
      const [toursData, carsData, guidesData, hotelsData, campSiteData, activityData] =
        await Promise.all([
          tourService.getAllTours({ limit: 100 }),
          serviceService.getAllServices({ type: 'car', limit: 100 }),
          serviceService.getAllServices({ type: 'guide', limit: 100 }),
          serviceService.getAllServices({ type: 'hotel', limit: 100 }),
          serviceService.getAllServices({ type: 'camp_site', limit: 100 }),
          serviceService.getAllServices({ type: 'activity', limit: 100 }),
        ])

      setTours(toursData.tours || [])
      setCars(carsData.services || [])
      setGuides(guidesData.services || [])
      setHotels(hotelsData.services || [])
      setCampSites(campSiteData.services || [])
      setActivityServices(activityData.services || [])
    } catch (error) {
      toast.error('Error loading data')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const saveSelections = () => {
    const selections = {
      selectedTour,
      selectedCar,
      selectedGuide,
      selectedHotel,
      selectedCampSite,
      selectedActivities,
      numberOfPersons,
      numberOfDays,
      startDate,
      couponCode,
      appliedDiscount,
    }
    localStorage.setItem(
      'pendingTourPlannerBooking',
      JSON.stringify({
        savedSelections: true,
        timestamp: new Date().toISOString(),
        ...selections,
      }),
    )
  }

  const restoreSelections = () => {
    const savedData = localStorage.getItem('pendingTourPlannerBooking')
    if (!savedData) {
      return
    }

    try {
      const parsed = JSON.parse(savedData)
      const savedTime = new Date(parsed.timestamp)
      const now = new Date()
      const hoursDiff = (now - savedTime) / (1000 * 60 * 60)

      if (hoursDiff >= 24 || !parsed.savedSelections) {
        return
      }

      ;[
        ['selectedTour', setSelectedTour],
        ['selectedCar', setSelectedCar],
        ['selectedGuide', setSelectedGuide],
        ['selectedHotel', setSelectedHotel],
        ['selectedCampSite', setSelectedCampSite],
        ['selectedActivities', setSelectedActivities],
        ['numberOfPersons', setNumberOfPersons],
        ['numberOfDays', setNumberOfDays],
        ['startDate', setStartDate],
        ['couponCode', setCouponCode],
        ['appliedDiscount', setAppliedDiscount],
      ].forEach(([key, setter]) => {
        const value = parsed[key]

        if (value !== undefined && value !== null && value !== '') {
          setter(value)
        }
      })
    } catch (error) {
      console.error('Error restoring selections:', error)
    }
  }

  const clearSavedSelections = () => {
    localStorage.removeItem('pendingTourPlannerBooking')
  }

  const calculatePricing = () => {
    let total = 0

    // Tour price (usually per person)
    if (selectedTour) {
      total += getBasePrice(selectedTour) * numberOfPersons
    }

    // Car price (usually per day)
    if (selectedCar) {
      total += getBasePrice(selectedCar) * numberOfDays
    }

    // Guide price (usually per day)
    if (selectedGuide) {
      total += getBasePrice(selectedGuide) * numberOfDays
    }

    // Hotel price (usually per day, may need to multiply by rooms)
    if (selectedHotel) {
      total += getBasePrice(selectedHotel) * numberOfDays * getHotelRoomsNeeded()
    }

    // Camp site price — base_price × days × ceil(persons / capacity)
    if (selectedCampSite) {
      const campCapacity = Math.max(1, Number(selectedCampSite.capacity) || 1)
      const campUnits = Math.ceil(numberOfPersons / campCapacity)
      total += getBasePrice(selectedCampSite) * numberOfDays * campUnits
    }

    // Activities price — package_price (fallback to base_price) × persons
    for (const activity of selectedActivities) {
      const price = Number(activity.package_price ?? activity.base_price ?? 0)
      total += price * numberOfPersons
    }

    setSubtotal(total)

    // Apply discount if exists — recalculate from discount parameters so amount stays correct if cart changes
    if (appliedDiscount) {
      let discountAmount = 0
      if (appliedDiscount.discount_type === 'percentage') {
        discountAmount = total * (Number.parseFloat(appliedDiscount.discount_value) / 100)
        if (
          appliedDiscount.max_discount_amount &&
          discountAmount > Number.parseFloat(appliedDiscount.max_discount_amount)
        ) {
          discountAmount = Number.parseFloat(appliedDiscount.max_discount_amount)
        }
      } else {
        discountAmount = Number.parseFloat(appliedDiscount.discount_value)
      }
      setFinalAmount(Math.max(0, total - discountAmount))
    } else {
      setFinalAmount(total)
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code')
      return
    }

    if (subtotal === 0) {
      toast.error('Please select at least one service')
      return
    }

    try {
      setValidatingCoupon(true)
      const response = await discountService.validateDiscount(couponCode, subtotal)

      if (response.success) {
        setAppliedDiscount(response.discount)
        toast.success(`Coupon applied! You saved ₹${response.discount.discount_amount}`)
      } else {
        toast.error(response.message || 'Invalid coupon code')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid or expired coupon code'
      toast.error(errorMessage)
      setAppliedDiscount(null)
    } finally {
      setValidatingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    setCouponCode('')
    setAppliedDiscount(null)
    toast.info('Coupon removed')
  }

  const handleBookNow = () => {
    if (!user) {
      // Save selections before redirecting to login
      saveSelections()
      toast.info('Please login to complete your booking')
      navigate('/login?redirect=tour-planner')
      return
    }

    if (
      !selectedTour &&
      !selectedCar &&
      !selectedGuide &&
      !selectedHotel &&
      !selectedCampSite &&
      selectedActivities.length === 0
    ) {
      toast.error('Please select at least one service')
      return
    }

    if (!startDate) {
      toast.error('Please select a start date')
      return
    }

    // Show confirmation modal
    setShowConfirmModal(true)
  }

  const confirmBooking = async () => {
    setShowConfirmModal(false)

    try {
      setLoading(true)

      const bookingData = {
        tour_id: selectedTour?.id || null,
        car_id: selectedCar?.id || null,
        guide_id: selectedGuide?.id || null,
        hotel_id: selectedHotel?.id || null,
        camp_site_id: selectedCampSite?.id || null,
        start_date: startDate,
        number_of_persons: numberOfPersons,
        number_of_days: numberOfDays,
        total_amount: finalAmount,
        discount_code: appliedDiscount ? couponCode : null,
        discount_amount: appliedDiscount ? Number.parseFloat(appliedDiscount.discount_amount) : 0,
        activities: selectedActivities.map((a) => ({
          service_id: a.id,
          participants: numberOfPersons,
        })),
        service_remarks: (() => {
          const remarks = {}
          if (serviceRemarks.car) remarks.car = serviceRemarks.car
          if (serviceRemarks.guide) remarks.guide = serviceRemarks.guide
          if (serviceRemarks.hotel) remarks.hotel = serviceRemarks.hotel
          if (serviceRemarks.camp_site) remarks.camp_site = serviceRemarks.camp_site
          const activityRemarks = selectedActivities
            .filter((a) => serviceRemarks[`activity_${a.id}`])
            .map((a) => ({ service_id: a.id, remarks: serviceRemarks[`activity_${a.id}`] }))
          if (activityRemarks.length > 0) remarks.activities = activityRemarks
          return Object.keys(remarks).length > 0 ? remarks : null
        })(),
      }

      const response = await bookingService.createBooking(bookingData)

      if (response.success) {
        clearSavedSelections()
        toast.success('Booking created successfully!')
        setTimeout(() => {
          navigate('/bookings')
        }, 2000)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error creating booking'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const toggleActivity = (activity) => {
    setSelectedActivities((prev) => {
      const exists = prev.find((a) => a.id === activity.id)
      return exists ? prev.filter((a) => a.id !== activity.id) : [...prev, activity]
    })
  }

  const sections = [
    {
      id: 'tour',
      label: 'Tour',
      icon: FaMapMarkedAlt,
      data: tours,
      selected: selectedTour,
      setter: setSelectedTour,
    },
    {
      id: 'car',
      label: 'Car',
      icon: FaCar,
      data: cars,
      selected: selectedCar,
      setter: setSelectedCar,
    },
    {
      id: 'guide',
      label: 'Guide',
      icon: FaUserTie,
      data: guides,
      selected: selectedGuide,
      setter: setSelectedGuide,
    },
    {
      id: 'hotel',
      label: 'Hotel',
      icon: FaHotel,
      data: hotels,
      selected: selectedHotel,
      setter: setSelectedHotel,
    },
    {
      id: 'camp_site',
      label: 'Camp Site',
      icon: FaCampground,
      data: campSites,
      selected: selectedCampSite,
      setter: setSelectedCampSite,
    },
    {
      id: 'activity',
      label: 'Activities',
      icon: FaHiking,
      data: activityServices,
      selected: selectedActivities.length > 0 ? selectedActivities : null,
      setter: toggleActivity,
      multiSelect: true,
    },
  ]

  const selectedCount = sections.filter((section) => section.selected).length

  const plannerHighlights = [
    { label: 'Live pricing', value: 'Instant total' },
    { label: 'Flexible add-ons', value: 'Mix tours + stays' },
    { label: 'Easy booking', value: user ? 'Ready to confirm' : 'Login when ready' },
  ]

  const typeIcons = {
    tour: FaMapMarkedAlt,
    car: FaCar,
    guide: FaUserTie,
    hotel: FaHotel,
    camp_site: FaCampground,
    activity: FaHiking,
  }

  const renderServiceCard = (item, isSelected, onSelect, type) => {
    const TypeIcon = typeIcons[type] || FaMountain
    const imageUrl = item.images?.[0] || item.image_url || item.photo

    return (
      <button
        type="button"
        onClick={() => onSelect(isSelected ? null : item)}
        className={`group w-full overflow-hidden rounded-3xl border text-left transition-all duration-300 ${
          isSelected
            ? 'border-primary-300 bg-gradient-to-br from-primary-50 via-white to-secondary-50 shadow-[0_18px_45px_-24px_rgba(14,165,233,0.55)] ring-2 ring-primary-200 scale-[1.01]'
            : 'border-gray-200 bg-white/90 shadow-sm hover:border-primary-200 hover:shadow-[0_18px_45px_-24px_rgba(15,23,42,0.28)]'
        }`}
      >
        <div className="flex flex-col sm:flex-row">
          {/* Image / Icon placeholder */}
          <div className="relative h-40 w-full shrink-0 overflow-hidden sm:h-auto sm:w-40 shine-on-hover">
            {imageUrl ? (
              <img src={imageUrl} alt={getItemLabel(item)} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary-100 to-secondary-100">
                <TypeIcon className="text-4xl text-primary-400" />
              </div>
            )}
            {isSelected && (
              <div className="absolute inset-0 flex items-center justify-center bg-primary-600/70 backdrop-blur-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg">
                  <FaCheck className="text-primary-600" size={18} />
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex flex-1 items-start justify-between gap-3 p-5">
            <div className="flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  <TypeIcon size={10} /> {type}
                </span>
                {item.duration && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                    <FaCalendarAlt size={10} /> {item.duration} day itinerary
                  </span>
                )}
                {item.rating && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                    <FaStar size={10} /> {item.rating}
                  </span>
                )}
              </div>
              <h4 className="mb-1 text-lg font-bold text-gray-900 transition-colors group-hover:text-primary-700">
                {getItemLabel(item)}
              </h4>
              <p className="mb-3 line-clamp-2 text-sm leading-relaxed text-gray-600">
                {item.description}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-base font-bold text-primary-600">
                  ₹{getBasePrice(item).toLocaleString()}
                  <span className="text-xs font-normal text-gray-500">
                    {type === 'tour'
                      ? '/person'
                      : `/${item.price_unit?.replace('_', ' ') || 'day'}`}
                  </span>
                </span>
                {item.capacity && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                    <FaUsers size={10} /> Capacity {item.capacity}
                  </span>
                )}
              </div>
            </div>
            <div
              className={`hidden sm:flex ml-2 mt-1 h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all ${
                isSelected
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                  : 'border-2 border-gray-300 bg-white text-transparent group-hover:border-primary-300'
              }`}
            >
              {isSelected && <FaCheck size={12} />}
            </div>
          </div>
        </div>
      </button>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50/40 py-8 sm:py-12">
      <Seo
        title="Plan Your Himachal Trip - Custom Itinerary Builder"
        description="Build your perfect Himachal Pradesh trip with our custom itinerary planner. Choose tours, hotels, cars, and guides. Get real-time pricing and book your personalized package."
        canonical="/tour-planner"
        keywords="custom tour Himachal Pradesh, plan Shimla trip, build itinerary Manali, personalized travel package India"
      />
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="relative mb-8 overflow-hidden rounded-[2rem] border border-primary-100 bg-gradient-to-br from-slate-950 via-primary-900 to-secondary-900 px-6 py-10 text-white shadow-[0_30px_80px_-32px_rgba(14,165,233,0.55)] sm:px-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.22),transparent_28%)]" />
          <div className="relative grid gap-8 lg:grid-cols-[1.4fr_0.8fr] lg:items-end">
            <div>
              <span className="mb-4 inline-flex items-center rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary-100 backdrop-blur-sm">
                Personalised itinerary builder
              </span>
              <h1 className="mb-4 text-4xl font-display font-bold leading-tight sm:text-5xl">
                Plan your perfect Himachal escape with fewer clicks and better vibes.
              </h1>
              <p className="max-w-2xl text-base text-sky-50/85 sm:text-lg">
                Mix tours, stays, guides, and rides into one polished booking flow. We&apos;ll
                handle the math while you chase the mountains.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              {plannerHighlights.map((highlight) => (
                <div
                  key={highlight.label}
                  className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-md"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-100/80">
                    {highlight.label}
                  </p>
                  <p className="mt-2 text-lg font-semibold text-white">{highlight.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/70 bg-white/80 p-2 shadow-lg shadow-slate-200/60 backdrop-blur-xl">
            <Button
              variant={plannerMode === 'package' ? 'toggle-active' : 'toggle-inactive'}
              icon={<FaTicketAlt />}
              onClick={() => setPlannerMode('package')}
            >
              Package Planner
            </Button>
            <Button
              variant={plannerMode === 'custom' ? 'toggle-active' : 'toggle-inactive'}
              icon={<FaRoute />}
              onClick={() => setPlannerMode('custom')}
            >
              Custom Map Planner
            </Button>
          </div>
        </div>

        {/* Conditional Rendering based on mode */}
        {plannerMode === 'custom' ? (
          <MapTripPlanner />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Panel - Service Selection */}
              <div className="lg:col-span-2 space-y-6">
                <div className="rounded-[1.75rem] border border-white/70 bg-white/80 p-5 shadow-lg shadow-slate-200/50 backdrop-blur-xl">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                        Build your package
                      </p>
                      <h2 className="mt-1 text-2xl font-bold text-gray-900">
                        Choose each trip ingredient
                      </h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {sections.map((section, index) => {
                        const SectionIcon = section.icon
                        let sectionBadgeClass =
                          'border border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:bg-primary-50 cursor-pointer'

                        if (section.selected) {
                          sectionBadgeClass =
                            'border border-primary-200 bg-primary-50 text-primary-700 ring-1 ring-primary-200'
                        } else if (activeSection === section.id) {
                          sectionBadgeClass =
                            'border border-slate-900 bg-slate-900 text-white shadow-md'
                        }

                        return (
                          <button
                            type="button"
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-300 ${sectionBadgeClass}`}
                          >
                            {(() => {
                              let stepClass = 'bg-gray-100 text-gray-600'
                              if (section.selected) stepClass = 'bg-primary-600 text-white'
                              else if (activeSection === section.id)
                                stepClass = 'bg-white/90 text-slate-900'
                              return (
                                <span
                                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${stepClass}`}
                                >
                                  {section.selected ? <FaCheck size={10} /> : index + 1}
                                </span>
                              )
                            })()}
                            <SectionIcon size={12} />
                            {section.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Service Selection Area */}
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                  {sections.map(
                    (section) =>
                      activeSection === section.id && (
                        <div key={section.id}>
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                                Current step
                              </p>
                              <h2 className="text-2xl font-bold text-gray-900">
                                Select {section.label}
                              </h2>
                            </div>
                            {section.selected && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                icon={<FaTrash />}
                                onClick={() =>
                                  section.multiSelect
                                    ? setSelectedActivities([])
                                    : section.setter(null)
                                }
                              >
                                Remove{section.multiSelect ? ' All' : ''}
                              </Button>
                            )}
                          </div>

                          {(() => {
                            if (loading) {
                              return (
                                <div className="flex flex-col items-center justify-center py-16">
                                  <div className="spinner mb-4"></div>
                                  <p className="text-sm font-medium text-gray-500">
                                    Loading {section.label.toLowerCase()}s…
                                  </p>
                                </div>
                              )
                            }

                            if (section.data.length === 0) {
                              return (
                                <div className="flex flex-col items-center py-12 text-center">
                                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50">
                                    <section.icon className="text-3xl text-gray-300" />
                                  </div>
                                  <p className="font-semibold text-gray-700">
                                    No {section.label.toLowerCase()}s available
                                  </p>
                                  <p className="mt-1 max-w-xs text-sm text-gray-400">
                                    Check back soon — we&apos;re always adding new options.
                                  </p>
                                </div>
                              )
                            }

                            return (
                              <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto">
                                {section.data.map((item) => {
                                  const isSelected = section.multiSelect
                                    ? selectedActivities.some((a) => a.id === item.id)
                                    : section.selected?.id === item.id
                                  return (
                                    <React.Fragment key={item.id}>
                                      {renderServiceCard(
                                        item,
                                        isSelected,
                                        section.setter,
                                        section.id,
                                      )}
                                    </React.Fragment>
                                  )
                                })}
                              </div>
                            )
                          })()}
                        </div>
                      ),
                  )}
                </div>
              </div>

              {/* Right Panel - Summary & Booking */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 overflow-hidden rounded-[1.9rem] border border-white/70 bg-white/85 shadow-[0_20px_60px_-32px_rgba(15,23,42,0.35)] backdrop-blur-xl">
                  <div className="border-b border-primary-100 bg-gradient-to-r from-primary-600 via-primary-500 to-secondary-500 p-6 text-white">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                          Booking summary
                        </p>
                        <h2 className="mt-2 flex items-center text-2xl font-bold">
                          <FaCalculator className="mr-2" />
                          Your mountain mix
                        </h2>
                      </div>
                      <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-right backdrop-blur-sm">
                        <p className="text-xs uppercase tracking-[0.16em] text-white/75">
                          Selected
                        </p>
                        <p className="text-2xl font-bold">
                          {selectedCount}/{sections.length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Booking Parameters */}
                    <div className="space-y-4 mb-6">
                      <div>
                        <label
                          htmlFor="planner-start-date"
                          className="label flex items-center gap-2"
                        >
                          <FaCalendarAlt className="text-primary-500" size={12} />
                          Start Date
                        </label>
                        <input
                          id="planner-start-date"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="planner-number-of-persons"
                          className="label flex items-center gap-2"
                        >
                          <FaUsers className="text-primary-500" size={12} />
                          Number of Persons
                        </label>
                        <input
                          id="planner-number-of-persons"
                          type="number"
                          min="1"
                          max="50"
                          value={numberOfPersons}
                          onChange={(e) =>
                            setNumberOfPersons(Number.parseInt(e.target.value, 10) || 1)
                          }
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="planner-number-of-days"
                          className="label flex items-center gap-2"
                        >
                          <FaRoute className="text-primary-500" size={12} />
                          Number of Days
                        </label>
                        <input
                          id="planner-number-of-days"
                          type="number"
                          min="1"
                          max="30"
                          value={numberOfDays}
                          onChange={(e) =>
                            setNumberOfDays(Number.parseInt(e.target.value, 10) || 1)
                          }
                          className="input-field"
                        />
                      </div>
                    </div>

                    {/* Selected Items */}
                    <div className="mb-4 space-y-2 rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                      {selectedTour && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                              <FaMapMarkedAlt size={12} />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {getItemLabel(selectedTour)}
                              </p>
                              <p className="text-xs text-gray-500">{numberOfPersons} person(s)</p>
                            </div>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-gray-900">
                            ₹{(getBasePrice(selectedTour) * numberOfPersons).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {selectedCar && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                              <FaCar size={12} />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {selectedCar.name}
                              </p>
                              <p className="text-xs text-gray-500">{numberOfDays} day(s)</p>
                            </div>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-gray-900">
                            ₹{(getBasePrice(selectedCar) * numberOfDays).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {selectedGuide && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                              <FaUserTie size={12} />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {selectedGuide.name}
                              </p>
                              <p className="text-xs text-gray-500">{numberOfDays} day(s)</p>
                            </div>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-gray-900">
                            ₹{(getBasePrice(selectedGuide) * numberOfDays).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {selectedHotel && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-600">
                              <FaHotel size={12} />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {selectedHotel.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {getHotelRoomsNeeded()} room(s) × {numberOfDays} day(s)
                              </p>
                            </div>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-gray-900">
                            ₹
                            {(
                              getBasePrice(selectedHotel) *
                              numberOfDays *
                              getHotelRoomsNeeded()
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {selectedCampSite && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                              <FaCampground size={12} />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {selectedCampSite.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {Math.ceil(
                                  numberOfPersons /
                                    Math.max(1, Number(selectedCampSite.capacity) || 1),
                                )}{' '}
                                unit(s) × {numberOfDays} day(s)
                              </p>
                            </div>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-gray-900">
                            ₹
                            {(
                              getBasePrice(selectedCampSite) *
                              numberOfDays *
                              Math.ceil(
                                numberOfPersons /
                                  Math.max(1, Number(selectedCampSite.capacity) || 1),
                              )
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {selectedActivities.map((activity) => {
                        const actPrice = Number(activity.package_price ?? activity.base_price ?? 0)
                        return (
                          <div
                            key={activity.id}
                            className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                                <FaHiking size={12} />
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900">
                                  {activity.name}
                                </p>
                                <p className="text-xs text-gray-500">{numberOfPersons} person(s)</p>
                              </div>
                            </div>
                            <p className="shrink-0 text-sm font-bold text-gray-900">
                              ₹{(actPrice * numberOfPersons).toLocaleString()}
                            </p>
                          </div>
                        )
                      })}

                      {!selectedTour &&
                        !selectedCar &&
                        !selectedGuide &&
                        !selectedHotel &&
                        !selectedCampSite &&
                        selectedActivities.length === 0 && (
                          <div className="flex flex-col items-center py-6 text-center">
                            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-50 to-secondary-50">
                              <FaMountain className="text-2xl text-primary-400" />
                            </div>
                            <p className="font-semibold text-gray-700">No services selected yet</p>
                            <p className="mt-1 text-sm text-gray-400">
                              Pick tours, stays, and rides to build your trip
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Subtotal */}
                    <div className="flex justify-between items-center mb-4 rounded-xl bg-gray-50 px-4 py-3">
                      <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                        Subtotal
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        ₹{subtotal.toLocaleString()}
                      </span>
                    </div>

                    {/* Service Remarks */}
                    {(selectedCar ||
                      selectedGuide ||
                      selectedHotel ||
                      selectedCampSite ||
                      selectedActivities.length > 0) && (
                      <div className="mb-4 space-y-3 rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Service remarks (optional)
                        </p>
                        {selectedCar && (
                          <input
                            type="text"
                            placeholder="Car remark (e.g., automatic)"
                            value={serviceRemarks.car || ''}
                            onChange={(e) =>
                              setServiceRemarks((prev) => ({ ...prev, car: e.target.value }))
                            }
                            maxLength={500}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                        )}
                        {selectedGuide && (
                          <input
                            type="text"
                            placeholder="Guide remark (e.g., Hindi-speaking)"
                            value={serviceRemarks.guide || ''}
                            onChange={(e) =>
                              setServiceRemarks((prev) => ({ ...prev, guide: e.target.value }))
                            }
                            maxLength={500}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                        )}
                        {selectedHotel && (
                          <input
                            type="text"
                            placeholder="Hotel remark (e.g., ground floor)"
                            value={serviceRemarks.hotel || ''}
                            onChange={(e) =>
                              setServiceRemarks((prev) => ({ ...prev, hotel: e.target.value }))
                            }
                            maxLength={500}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                        )}
                        {selectedCampSite && (
                          <input
                            type="text"
                            placeholder="Camp site remark (e.g., near stream)"
                            value={serviceRemarks.camp_site || ''}
                            onChange={(e) =>
                              setServiceRemarks((prev) => ({ ...prev, camp_site: e.target.value }))
                            }
                            maxLength={500}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                        )}
                        {selectedActivities.map((activity) => (
                          <input
                            key={activity.id}
                            type="text"
                            placeholder={`${activity.name} remark`}
                            value={serviceRemarks[`activity_${activity.id}`] || ''}
                            onChange={(e) =>
                              setServiceRemarks((prev) => ({
                                ...prev,
                                [`activity_${activity.id}`]: e.target.value,
                              }))
                            }
                            maxLength={500}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                        ))}
                      </div>
                    )}

                    {/* Coupon Code */}
                    <div className="mb-4">
                      <label className="label flex items-center gap-2">
                        <FaTicketAlt className="text-primary-500" size={12} />
                        Coupon Code
                      </label>
                      {appliedDiscount ? (
                        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
                              <FaCheck size={10} /> {appliedDiscount.code}
                            </span>
                            <button
                              onClick={handleRemoveCoupon}
                              className="flex h-7 w-7 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 hover:text-red-700"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                          <p className="text-sm text-green-600">{appliedDiscount.description}</p>
                          <div className="mt-2 flex justify-between items-center">
                            <span className="text-sm text-gray-600">Discount</span>
                            <span className="text-green-700 font-bold">
                              -₹
                              {Number.parseFloat(appliedDiscount.discount_amount).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Enter coupon code"
                            className="input-field"
                            disabled={validatingCoupon}
                          />
                          <Button
                            variant="secondary"
                            onClick={handleApplyCoupon}
                            disabled={validatingCoupon || !couponCode.trim() || subtotal === 0}
                            className="whitespace-nowrap"
                          >
                            {validatingCoupon ? 'Checking...' : 'Apply'}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Final Total */}
                    <div className="mb-6 rounded-2xl bg-gradient-to-br from-primary-50 via-white to-secondary-50 p-4 ring-1 ring-primary-100">
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-gray-900">Total Amount</span>
                        <span className="text-2xl font-bold text-primary-600">
                          ₹{finalAmount.toLocaleString()}
                        </span>
                      </div>
                      {appliedDiscount && (
                        <p className="text-sm text-green-600 mt-1">
                          You saved ₹
                          {Number.parseFloat(appliedDiscount.discount_amount).toLocaleString()}!
                        </p>
                      )}
                    </div>

                    {/* Book Now Button */}
                    <Button
                      onClick={handleBookNow}
                      disabled={subtotal === 0 || !startDate}
                      loading={loading}
                      fullWidth
                      className="justify-center"
                    >
                      Reserve Your Spot
                    </Button>

                    {!user && (
                      <p className="text-sm text-gray-500 text-center mt-2">
                        Please <span className="text-primary-600 font-semibold">login</span> to
                        complete booking
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-[2rem] border border-white/60 bg-white shadow-[0_32px_80px_-20px_rgba(15,23,42,0.5)]">
                  {/* Modal header */}
                  <div className="relative border-b border-primary-100 bg-gradient-to-r from-primary-600 via-primary-500 to-secondary-500 px-6 py-6 text-white">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_40%)]" />
                    <div className="relative flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">
                          Almost there
                        </p>
                        <h2 className="mt-1 text-2xl font-display font-bold">
                          Confirm Your Booking
                        </h2>
                      </div>
                      <button
                        onClick={() => setShowConfirmModal(false)}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white"
                      >
                        &times;
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[calc(90vh-12rem)] overflow-y-auto p-6">
                    <div className="space-y-4 mb-6">
                      {/* Booking Details */}
                      <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-5">
                        <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
                          <FaCalendarAlt className="text-primary-500" size={14} />
                          Trip Details
                        </h3>
                        <div className="grid gap-3 text-sm sm:grid-cols-3">
                          <div className="rounded-xl bg-white p-3 text-center shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                              Start Date
                            </p>
                            <p className="mt-1 text-base font-bold text-gray-900">
                              {new Date(startDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="rounded-xl bg-white p-3 text-center shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                              Persons
                            </p>
                            <p className="mt-1 text-base font-bold text-gray-900">
                              {numberOfPersons}
                            </p>
                          </div>
                          <div className="rounded-xl bg-white p-3 text-center shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                              Days
                            </p>
                            <p className="mt-1 text-base font-bold text-gray-900">{numberOfDays}</p>
                          </div>
                        </div>
                      </div>

                      {/* Selected Services */}
                      <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-5">
                        <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
                          <FaMapMarkedAlt className="text-primary-500" size={14} />
                          Selected Services
                        </h3>
                        <div className="space-y-3">
                          {selectedTour && (
                            <div className="flex justify-between items-center rounded-xl bg-white p-3 shadow-sm">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                                  <FaMapMarkedAlt size={14} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {getItemLabel(selectedTour)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Tour · {numberOfPersons} person(s)
                                  </p>
                                </div>
                              </div>
                              <p className="text-sm font-bold text-gray-900">
                                ₹{(getBasePrice(selectedTour) * numberOfPersons).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {selectedCar && (
                            <div className="flex justify-between items-center rounded-xl bg-white p-3 shadow-sm">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                                  <FaCar size={14} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {selectedCar.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Car · {numberOfDays} day(s)
                                  </p>
                                </div>
                              </div>
                              <p className="text-sm font-bold text-gray-900">
                                ₹{(getBasePrice(selectedCar) * numberOfDays).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {selectedGuide && (
                            <div className="flex justify-between items-center rounded-xl bg-white p-3 shadow-sm">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                                  <FaUserTie size={14} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {selectedGuide.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Guide · {numberOfDays} day(s)
                                  </p>
                                </div>
                              </div>
                              <p className="text-sm font-bold text-gray-900">
                                ₹{(getBasePrice(selectedGuide) * numberOfDays).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {selectedHotel && (
                            <div className="flex justify-between items-center rounded-xl bg-white p-3 shadow-sm">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-50 text-green-600">
                                  <FaHotel size={14} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {selectedHotel.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Hotel · {getHotelRoomsNeeded()} room(s) × {numberOfDays} day(s)
                                  </p>
                                </div>
                              </div>
                              <p className="text-sm font-bold text-gray-900">
                                ₹
                                {(
                                  getBasePrice(selectedHotel) *
                                  numberOfDays *
                                  getHotelRoomsNeeded()
                                ).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {selectedCampSite && (
                            <div className="flex justify-between items-center rounded-xl bg-white p-3 shadow-sm">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                                  <FaCampground size={14} />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {selectedCampSite.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Camp Site ·{' '}
                                    {Math.ceil(
                                      numberOfPersons /
                                        Math.max(1, Number(selectedCampSite.capacity) || 1),
                                    )}{' '}
                                    unit(s) × {numberOfDays} day(s)
                                  </p>
                                </div>
                              </div>
                              <p className="text-sm font-bold text-gray-900">
                                ₹
                                {(
                                  getBasePrice(selectedCampSite) *
                                  numberOfDays *
                                  Math.ceil(
                                    numberOfPersons /
                                      Math.max(1, Number(selectedCampSite.capacity) || 1),
                                  )
                                ).toLocaleString()}
                              </p>
                            </div>
                          )}

                          {selectedActivities.map((activity) => {
                            const actPrice = Number(
                              activity.package_price ?? activity.base_price ?? 0,
                            )
                            return (
                              <div
                                key={activity.id}
                                className="flex justify-between items-center rounded-xl bg-white p-3 shadow-sm"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                                    <FaHiking size={14} />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                      {activity.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Activity · {numberOfPersons} person(s)
                                    </p>
                                  </div>
                                </div>
                                <p className="text-sm font-bold text-gray-900">
                                  ₹{(actPrice * numberOfPersons).toLocaleString()}
                                </p>
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      {/* Pricing Summary */}
                      <div className="rounded-2xl bg-gradient-to-br from-primary-50 via-white to-secondary-50 p-5 ring-1 ring-primary-100">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="font-semibold text-gray-900">
                              ₹{subtotal.toLocaleString()}
                            </span>
                          </div>
                          {appliedDiscount && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span className="flex items-center gap-1">
                                <FaTicketAlt size={10} /> {appliedDiscount.code}
                              </span>
                              <span className="font-semibold">
                                -₹
                                {Number.parseFloat(
                                  appliedDiscount.discount_amount,
                                ).toLocaleString()}
                              </span>
                            </div>
                          )}
                          <div className="border-t border-primary-200/60 pt-3 mt-3">
                            <div className="flex justify-between items-center">
                              <span className="text-xl font-bold text-gray-900">Total Amount</span>
                              <span className="text-2xl font-bold text-primary-600">
                                ₹{finalAmount.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                  </div>
                  <div className="flex gap-3 border-t border-gray-100 p-6">
                    <Button
                      variant="secondary"
                      onClick={() => setShowConfirmModal(false)}
                      disabled={loading}
                      className="flex-1"
                    >
                      Go Back
                    </Button>
                    <Button onClick={confirmBooking} loading={loading} className="flex-1">
                      Confirm Booking
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TourPlanner
